import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Building2, User, Phone, Mail, ExternalLink, FileText, Image, Loader2, Calendar, DollarSign, ChevronRight, Share2, Copy, Check, Home, Clock, AlertTriangle, Briefcase } from 'lucide-react';

interface Job {
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  address?: string;
  city: string;
  state?: string;
  zip_code?: string;
  general_contractor?: string;
  project_manager?: string;
  pm_phone?: string;
  pm_email?: string;
  lat: number;
  lng: number;
  status?: string;
  description?: string;
  start_date?: string;
  estimated_completion?: string;
  contract_value?: number;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export const JobDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [language, setLanguage] = useState<'en' | 'es'>('en');
  const [copied, setCopied] = useState(false);

  // Get current user for permission checks
  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('bfa_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  };

  const currentUser = getCurrentUser();
  const canViewPhotosAndReports = currentUser?.role === 'admin' || currentUser?.role === 'foreman';
  const isTruckDriver = currentUser?.role === 'truck_driver' || currentUser?.role === 'cdl_driver';

  // Translation system
  const getTranslations = (lang: 'en' | 'es') => ({
    en: {
      loading: 'Loading job details...',
      jobNotFound: 'Job not found',
      back: 'Back to Jobs',
      jobNumber: 'Job Number',
      location: 'Location',
      contractor: 'General Contractor',
      projectManager: 'Project Manager',
      phone: 'Phone',
      email: 'Email',
      status: 'Status',
      description: 'Description',
      startDate: 'Start Date',
      estimatedCompletion: 'Estimated Completion',
      contractValue: 'Contract Value',
      openInMaps: 'Open in Maps',
      shareAddress: 'Share Address',
      callPM: 'Call Project Manager',
      emailPM: 'Email Project Manager',
      viewPlans: 'View Plans',
      viewPhotos: 'View Photos',
      viewReports: 'View Daily Reports'
    },
    es: {
      loading: 'Cargando detalles del trabajo...',
      jobNotFound: 'Trabajo no encontrado',
      back: 'Volver a Trabajos',
      jobNumber: 'Número de Trabajo',
      location: 'Ubicación',
      contractor: 'Contratista General',
      projectManager: 'Gerente de Proyecto',
      phone: 'Teléfono',
      email: 'Correo',
      status: 'Estado',
      description: 'Descripción',
      startDate: 'Fecha de Inicio',
      estimatedCompletion: 'Finalización Estimada',
      contractValue: 'Valor del Contrato',
      openInMaps: 'Abrir en Mapas',
      shareAddress: 'Compartir Dirección',
      callPM: 'Llamar Gerente de Proyecto',
      emailPM: 'Enviar Email al Gerente',
      viewPlans: 'Ver Planos',
      viewPhotos: 'Ver Fotos',
      viewReports: 'Ver Reportes Diarios'
    }
  }[lang]);

  const t = getTranslations(language);

  // API call function
  const apiCall = async (endpoint: string) => {
    const token = localStorage.getItem('bfa_token');

    if (!token) {
      throw new Error('No authentication token');
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 401) {
      localStorage.removeItem('bfa_token');
      localStorage.removeItem('bfa_user');
      window.location.href = '/login';
      throw new Error('Authentication expired');
    }

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || `API request failed: ${response.status}`);
    }

    return response.json();
  };

  // Load job details
  const loadJobDetails = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await apiCall(`/api/jobs/${id}`);

      if (response.success && response.data) {
        setJob(response.data);
      } else {
        setError(t.jobNotFound);
      }
    } catch (err: any) {
      console.error('Failed to load job details:', err);
      setError(err.message || 'Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  // Build full address string
  const getFullAddress = () => {
    if (!job) return '';
    const parts = [];
    if (job.address) parts.push(job.address);
    if (job.city) parts.push(job.city);
    if (job.state) parts.push(job.state);
    if (job.zip_code) parts.push(job.zip_code);
    return parts.join(', ');
  };

  // Quick copy address to clipboard
  const copyAddress = async () => {
    const fullAddress = getFullAddress();
    if (!fullAddress) return;

    try {
      await navigator.clipboard.writeText(fullAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Open address in maps app - prioritize address, fallback to lat/lng
  const openInMaps = () => {
    if (!job) return;

    // Detect iOS (including iPadOS 13+)
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent) ||
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /android/.test(userAgent);

    // Use address by default for better readability in maps
    if (job.address) {
      const address = encodeURIComponent(getFullAddress());

      if (isIOS) {
        // Open in Apple Maps using address
        window.location.href = `maps://?q=${address}`;
      } else if (isAndroid) {
        // Open in Google Maps app using address
        window.location.href = `geo:0,0?q=${address}`;
      } else {
        // Desktop - open Google Maps in browser
        window.open(`https://maps.google.com/?q=${address}`, '_blank');
      }
    } else if (job.lat && job.lng) {
      // Fallback to coordinates if no address available
      if (isIOS) {
        // Open in Apple Maps using coordinates
        window.location.href = `maps://?q=${job.job_name}&ll=${job.lat},${job.lng}`;
      } else if (isAndroid) {
        // Open in Google Maps app using coordinates
        window.location.href = `geo:${job.lat},${job.lng}?q=${job.lat},${job.lng}(${encodeURIComponent(job.job_name)})`;
      } else {
        // Desktop - open Google Maps in browser with coordinates
        window.open(`https://maps.google.com/?q=${job.lat},${job.lng}`, '_blank');
      }
    }
  };

  // Share address using Web Share API or clipboard fallback
  const shareAddress = async () => {
    if (!job) return;

    const fullAddress = getFullAddress();

    // Build a nicely formatted message for texting
    // Use Apple Maps link (works on both iOS and Android - Android will offer to open in Google Maps)
    const mapsUrl = job.address
      ? `https://maps.apple.com/?q=${encodeURIComponent(fullAddress)}`
      : (job.lat && job.lng ? `https://maps.apple.com/?ll=${job.lat},${job.lng}&q=${encodeURIComponent(job.job_name)}` : '');

    const shareText = [
      `📍 ${job.job_name}`,
      '',
      fullAddress,
      '',
      mapsUrl
    ].filter(line => line !== '' || line === '').join('\n').trim();

    // Try Web Share API first (works on iOS Safari, Android Chrome)
    if (navigator.share) {
      try {
        await navigator.share({
          title: job.job_name,
          text: shareText
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fall through to clipboard
        if ((err as Error).name === 'AbortError') return;
      }
    }

    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(shareText);
      alert(language === 'es' ? 'Dirección copiada al portapapeles' : 'Address copied to clipboard');
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  // Call project manager
  const callProjectManager = () => {
    if (job?.pm_phone) {
      window.open(`tel:${job.pm_phone}`, '_blank');
    }
  };

  // Email project manager
  const emailProjectManager = () => {
    if (job?.pm_email) {
      window.open(`mailto:${job.pm_email}`, '_blank');
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  useEffect(() => {
    const storedLang = localStorage.getItem('language') || 'en';
    setLanguage(storedLang as 'en' | 'es');

    loadJobDetails();

    // Listen for language changes
    const handleLanguageChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      const newLang = (customEvent.detail || 'en') as 'en' | 'es';
      setLanguage(newLang);
    };

    window.addEventListener('languageChange', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, [id]);

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f0f2f5'
    },
    headerSection: {
      backgroundColor: '#2c2c3e',
      color: 'white',
      padding: '20px',
      paddingTop: '16px'
    },
    headerTop: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px'
    },
    backButton: {
      padding: '10px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      color: 'white',
      transition: 'all 0.2s',
      flexShrink: 0,
      marginTop: '2px'
    },
    headerContent: {
      flex: 1
    },
    title: {
      fontSize: '22px',
      fontWeight: '600',
      color: 'white',
      margin: 0,
      lineHeight: '1.3'
    },
    jobNumberBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 14px',
      backgroundColor: 'rgba(99, 102, 241, 0.3)',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: '600',
      color: 'white',
      marginTop: '12px'
    },
    contentSection: {
      padding: '16px 20px',
      paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      border: '1px solid #e5e7eb',
      marginBottom: '12px'
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '16px',
      paddingBottom: '12px',
      borderBottom: '1px solid #f0f2f5'
    },
    cardIconWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      backgroundColor: '#eff6ff',
      borderRadius: '10px',
      flexShrink: 0
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      margin: 0
    },
    infoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: '12px 0',
      borderBottom: '1px solid #f0f2f5'
    },
    infoRowLast: {
      borderBottom: 'none'
    },
    label: {
      fontSize: '16px',
      color: '#6b7280',
      fontWeight: '500'
    },
    value: {
      fontSize: '17px',
      color: '#1f2937',
      fontWeight: '600',
      textAlign: 'right' as const,
      flex: 1,
      marginLeft: '16px',
      lineHeight: '1.4'
    },
    actionButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      padding: '18px',
      backgroundColor: 'white',
      color: '#1f2937',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '17px',
      fontWeight: '600',
      marginBottom: '10px',
      width: '100%',
      transition: 'all 0.2s',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
    },
    actionButtonContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    actionIconWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      flexShrink: 0
    },
    primaryAction: {
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none'
    },
    description: {
      fontSize: '14px',
      color: '#374151',
      lineHeight: '1.6',
      backgroundColor: '#f9fafb',
      padding: '14px',
      borderRadius: '10px',
      border: '1px solid #e5e7eb'
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      gap: '16px'
    },
    loadingSpinner: {
      color: '#3b82f6',
      animation: 'spin 1s linear infinite'
    },
    loadingText: {
      fontSize: '16px',
      color: '#6b7280',
      fontWeight: '500'
    },
    error: {
      margin: '20px',
      padding: '20px',
      fontSize: '15px',
      color: '#dc2626',
      backgroundColor: '#fef2f2',
      borderRadius: '12px',
      border: '1px solid #fecaca',
      textAlign: 'center' as const
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      backgroundColor: '#dcfce7',
      color: '#16a34a',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '700',
      textTransform: 'uppercase' as const
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <Loader2 size={40} style={styles.loadingSpinner} />
          <p style={styles.loadingText}>{t.loading}</p>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error || t.jobNotFound}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Dark Header Section */}
      <div style={styles.headerSection}>
        <div style={styles.headerTop}>
          <div style={styles.headerContent}>
            <h1 style={styles.title}>{job.job_name}</h1>
            <div style={styles.jobNumberBadge}>
              {t.jobNumber}: {job.bradanna_job_number}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.contentSection}>
        {/* Location Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{...styles.cardIconWrapper, backgroundColor: '#dcfce7'}}>
              <MapPin size={20} color="#16a34a" />
            </div>
            <h3 style={styles.cardTitle}>{t.location}</h3>
          </div>
          <div style={{...styles.infoRow, ...styles.infoRowLast}}>
            <span style={styles.label}>Address</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, marginLeft: '16px', justifyContent: 'flex-end' }}>
              <span style={{...styles.value, margin: 0, flex: 1}}>
                {job.address ? `${job.address}, ` : ''}{job.city}{job.state ? `, ${job.state}` : ''}{job.zip_code ? ` ${job.zip_code}` : ''}
              </span>
              <button
                onClick={copyAddress}
                style={{
                  padding: '8px',
                  backgroundColor: copied ? '#dcfce7' : '#f3f4f6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  flexShrink: 0
                }}
                title={language === 'es' ? 'Copiar dirección' : 'Copy address'}
              >
                {copied ? (
                  <Check size={16} color="#16a34a" />
                ) : (
                  <Copy size={16} color="#6b7280" />
                )}
              </button>
            </div>
          </div>
          {/* Show Open in Maps if address OR coordinates available */}
          {(job.address || (job.lat && job.lng)) && (
            <button
              style={styles.actionButton}
              onClick={openInMaps}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <div style={styles.actionButtonContent}>
                <div style={{...styles.actionIconWrapper, backgroundColor: '#dcfce7'}}>
                  <ExternalLink size={18} color="#16a34a" />
                </div>
                <span>{t.openInMaps}</span>
              </div>
              <ChevronRight size={20} color="#9ca3af" />
            </button>
          )}
          {/* Share Address button */}
          {job.address && (
            <button
              style={styles.actionButton}
              onClick={shareAddress}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <div style={styles.actionButtonContent}>
                <div style={{...styles.actionIconWrapper, backgroundColor: '#eff6ff'}}>
                  <Share2 size={18} color="#3b82f6" />
                </div>
                <span>{t.shareAddress}</span>
              </div>
              <ChevronRight size={20} color="#9ca3af" />
            </button>
          )}
        </div>

        {/* Contractor Card */}
        {job.general_contractor && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={{...styles.cardIconWrapper, backgroundColor: '#fef3c7'}}>
                <Building2 size={20} color="#d97706" />
              </div>
              <h3 style={styles.cardTitle}>{t.contractor}</h3>
            </div>
            <div style={{...styles.infoRow, ...styles.infoRowLast}}>
              <span style={styles.label}>Company</span>
              <span style={styles.value}>{job.general_contractor}</span>
            </div>
          </div>
        )}

        {/* Project Manager Card */}
        {job.project_manager && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={{...styles.cardIconWrapper, backgroundColor: '#e0e7ff'}}>
                <User size={20} color="#4f46e5" />
              </div>
              <h3 style={styles.cardTitle}>{t.projectManager}</h3>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>Name</span>
              <span style={styles.value}>{job.project_manager}</span>
            </div>
            {job.pm_phone && (
              <div style={styles.infoRow}>
                <span style={styles.label}>{t.phone}</span>
                <span style={styles.value}>{job.pm_phone}</span>
              </div>
            )}
            {job.pm_email && (
              <div style={{...styles.infoRow, ...styles.infoRowLast}}>
                <span style={styles.label}>{t.email}</span>
                <span style={styles.value}>{job.pm_email}</span>
              </div>
            )}

            {/* Action buttons for PM contact */}
            {job.pm_phone && (
              <button
                style={styles.actionButton}
                onClick={callProjectManager}
              >
                <div style={styles.actionButtonContent}>
                  <div style={{...styles.actionIconWrapper, backgroundColor: '#dcfce7'}}>
                    <Phone size={18} color="#16a34a" />
                  </div>
                  <span>{t.callPM}</span>
                </div>
                <ChevronRight size={20} color="#9ca3af" />
              </button>
            )}
            {job.pm_email && (
              <button
                style={styles.actionButton}
                onClick={emailProjectManager}
              >
                <div style={styles.actionButtonContent}>
                  <div style={{...styles.actionIconWrapper, backgroundColor: '#eff6ff'}}>
                    <Mail size={18} color="#3b82f6" />
                  </div>
                  <span>{t.emailPM}</span>
                </div>
                <ChevronRight size={20} color="#9ca3af" />
              </button>
            )}
          </div>
        )}

        {/* Quick Actions Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{...styles.cardIconWrapper, backgroundColor: '#fce7f3'}}>
              <FileText size={20} color="#db2777" />
            </div>
            <h3 style={styles.cardTitle}>Quick Actions</h3>
          </div>

          <button
            style={styles.actionButton}
            onClick={() => {
              navigate('/plans', {
                state: {
                  jobName: job.job_name,
                  jobNumber: job.bradanna_job_number
                }
              });
            }}
          >
            <div style={styles.actionButtonContent}>
              <div style={{...styles.actionIconWrapper, backgroundColor: '#fef3c7'}}>
                <FileText size={18} color="#d97706" />
              </div>
              <span>{t.viewPlans}</span>
            </div>
            <ChevronRight size={20} color="#9ca3af" />
          </button>

          {/* Photos and Reports Buttons - Only for Admin/Foreman */}
          {canViewPhotosAndReports && (
            <>
              <button
                style={styles.actionButton}
                onClick={() => {
                  navigate('/photos', {
                    state: {
                      jobId: job.job_id,
                      jobName: job.job_name,
                      jobNumber: job.bradanna_job_number
                    }
                  });
                }}
              >
                <div style={styles.actionButtonContent}>
                  <div style={{...styles.actionIconWrapper, backgroundColor: '#eff6ff'}}>
                    <Image size={18} color="#3b82f6" />
                  </div>
                  <span>{t.viewPhotos}</span>
                </div>
                <ChevronRight size={20} color="#9ca3af" />
              </button>

              <button
                style={styles.actionButton}
                onClick={() => {
                  navigate('/view-daily-reports', {
                    state: {
                      jobId: job.job_id,
                      jobName: job.job_name,
                      jobNumber: job.bradanna_job_number
                    }
                  });
                }}
              >
                <div style={styles.actionButtonContent}>
                  <div style={{...styles.actionIconWrapper, backgroundColor: '#dcfce7'}}>
                    <FileText size={18} color="#16a34a" />
                  </div>
                  <span>{t.viewReports}</span>
                </div>
                <ChevronRight size={20} color="#9ca3af" />
              </button>
            </>
          )}
        </div>

        {/* Job Information Card */}
        {(job.status || job.start_date || job.estimated_completion || job.contract_value) && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={{...styles.cardIconWrapper, backgroundColor: '#f3e8ff'}}>
                <Calendar size={20} color="#9333ea" />
              </div>
              <h3 style={styles.cardTitle}>Job Information</h3>
            </div>
            {job.status && (
              <div style={styles.infoRow}>
                <span style={styles.label}>{t.status}</span>
                <span style={styles.statusBadge}>{job.status}</span>
              </div>
            )}
            {job.start_date && (
              <div style={styles.infoRow}>
                <span style={styles.label}>{t.startDate}</span>
                <span style={styles.value}>{formatDate(job.start_date)}</span>
              </div>
            )}
            {job.estimated_completion && (
              <div style={styles.infoRow}>
                <span style={styles.label}>{t.estimatedCompletion}</span>
                <span style={styles.value}>{formatDate(job.estimated_completion)}</span>
              </div>
            )}
            {job.contract_value && (
              <div style={{...styles.infoRow, ...styles.infoRowLast}}>
                <span style={styles.label}>{t.contractValue}</span>
                <span style={styles.value}>{formatCurrency(job.contract_value)}</span>
              </div>
            )}
          </div>
        )}

        {/* Description Card */}
        {job.description && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={{...styles.cardIconWrapper, backgroundColor: '#f0fdf4'}}>
                <FileText size={20} color="#16a34a" />
              </div>
              <h3 style={styles.cardTitle}>{t.description}</h3>
            </div>
            <div style={styles.description}>{job.description}</div>
          </div>
        )}
      </div>

      {/* CDL Driver Bottom Navigation */}
      {isTruckDriver && (
        <nav style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'white',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          padding: '8px 0 12px',
          zIndex: 1000,
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)'
        }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 12px',
              cursor: 'pointer',
              color: '#6b7280'
            }}
          >
            <Home size={24} />
            <span style={{ fontSize: '11px', fontWeight: '600' }}>Home</span>
          </button>

          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 12px',
              cursor: 'pointer',
              color: '#6b7280'
            }}
          >
            <Clock size={24} />
            <span style={{ fontSize: '11px', fontWeight: '600' }}>Full Log</span>
          </button>

          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 12px',
              cursor: 'pointer',
              color: '#6b7280'
            }}
          >
            <Calendar size={24} />
            <span style={{ fontSize: '11px', fontWeight: '600' }}>Weekly Hrs</span>
          </button>

          <button
            onClick={() => navigate('/equipment-issue')}
            style={{
              background: 'none',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 12px',
              cursor: 'pointer',
              color: '#ef4444'
            }}
          >
            <AlertTriangle size={24} />
            <span style={{ fontSize: '11px', fontWeight: '600' }}>Report Issue</span>
          </button>

          <button
            onClick={() => navigate('/cdl-jobs')}
            style={{
              background: 'none',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 12px',
              cursor: 'pointer',
              color: '#3b82f6'
            }}
          >
            <Briefcase size={24} />
            <span style={{ fontSize: '11px', fontWeight: '600' }}>Jobs</span>
          </button>
        </nav>
      )}
    </div>
  );
};

export default JobDetails;